import React from 'react';
import PropTypes from 'prop-types';
import {
    Grid,
    Box,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Collapse,
    Paper,
    Alert,
    Toolbar,
} from '@mui/material';
import {
    Input,
    Select,
    WeightInput,
    SecondaryButton,
    CommonModal,
    SearchableDropdown,
} from '../../Common';
import { lightGrayColor } from '../../../color_utils';
import { trakrefFontFamily } from '../../../utils';
import { renderWeight } from '../../../Utils/weight_utils';
import { TranslateW, Translate } from "../../../Utils/Languages/Translate";

const GasModal = ({
    open,
    header,
    type,
    buttonText,
    assetDetails: {
        name,
        maxGasCapacity: assetMaxGasCapacity,
        currentGasWeightLbs: assetCurrentGasWeight,
        materialTypeName: assetMaterialType,
    },
    selectedCylinder,
    setSelectedCylinder,
    cylinderNotes,
    setCylinderNotes,
    transferNotes,
    setTransferNotes,
    recoverAmount,
    closeModal,
    onClick,
    onChange,
    cylinders,
    materialStatusesDropdown,
    setMaterialStatuses,
    materialStatuses,
    recoverGasStatusDropdown,
    recoverGasStatus,
    setRecoverGasStatus,
    isRetroFit,
    formState: {
        newGasChargeCapacityLbs
    } = {},
    cylindersAlreadySelected = [],
    totalTransferAmount,
}) => {
    const {
        maxGasCapacityLbs = '',
        currentGasWeightLbs = '',
        materialType = '',
        id = null,
    } = selectedCylinder || {};

    const isUnknownCylinder = id === 0;
    // if selectedCylinder is "Unknown" (id = 0), set properties to N/A
    const maxGasCapacity = isUnknownCylinder ? "N/A" : renderWeight(maxGasCapacityLbs);
    const currentGasWeight = isUnknownCylinder ? "N/A" : renderWeight(currentGasWeightLbs);
    const finalMaterialType = isUnknownCylinder ? "N/A" : materialType;
    const isInstall = type === 'install';
    const isRecover = type === "recover";

    // recoverAmount and assetMaxGasCapacity are both in lbs; compare in lbs (no display conversion)
    const transferAmountGreaterThanAssetMaxCapacity = !isRetroFit && ((recoverAmount + totalTransferAmount) > getConvertedWeight(assetMaxGasCapacity));
    const transferAmountGreaterThanNewAssetMaxCapacity = isRetroFit && (recoverAmount > newGasChargeCapacityLbs);
    const transferAmountGreaterThanCylinderWeight = (
        type === 'recover'
        && !isUnknownCylinder
        && recoverAmount > 0
        && (recoverAmount + currentGasWeightLbs) > maxGasCapacityLbs
    );
    const recoverGasError = (
        type === 'recover'
        && !isUnknownCylinder
        && transferAmountGreaterThanAssetMaxCapacity
    );
    const installGasError = (
        type === 'install'
        && !isUnknownCylinder
        && id
        && recoverAmount > 0
        && (
            recoverAmount > currentGasWeightLbs
            || transferAmountGreaterThanAssetMaxCapacity
            || transferAmountGreaterThanNewAssetMaxCapacity
        )
    );
    // for recovering gas, user cannot install more gas than the max gas capacity
    const commonGasErrors = (
        id === undefined
        || id ===  null
        || recoverAmount === null
        || recoverAmount === 0
        || isNaN(recoverAmount)
    );

    const disableSubmitButton = (
        commonGasErrors
        || recoverGasError
        || installGasError
        || transferAmountGreaterThanCylinderWeight
        || transferAmountGreaterThanAssetMaxCapacity
    );

    const cylinderColumns = [
        { label: "Max Gas Capacity", value: maxGasCapacity },
        { label: "Current Weight", value: currentGasWeight },
        { label: "Material Type", value: finalMaterialType }
    ];

    const assetColumns = [
        { label: "Max Gas Capacity", value: renderWeight(assetMaxGasCapacity) },
        { label: "Current Weight", value: renderWeight(assetCurrentGasWeight) },
        { label: "Material Type", value: assetMaterialType }
    ];

    const resetState = () => {
        setSelectedCylinder({ id: null, name: '' });
        if (isInstall) {
            setMaterialStatuses({ id: null, name: '' });
        }
        setCylinderNotes('');
        setTransferNotes('');
        onChange(null);
    };

    const handleClick = () => {
        onClick();
        return resetState();
    };

    const handleCylinderSelect = (selectedCylinderID) => {
        const selected = cylinders.find(cyl => cyl.id === selectedCylinderID) || { id: null };

        if (selectedCylinderID !== 0 && isInstall) {
            setMaterialStatuses({ id: null, name: '' });
        }
        if (selectedCylinderID !== 0 && isRecover) {
            setRecoverGasStatus({ id: null, name: '' });
        }
        return setSelectedCylinder(selected);
    };

    const handleMaterialStatusSelect = (selectedMaterialStatusID) => {
        const selected = materialStatusesDropdown.find(mts => mts.id === selectedMaterialStatusID) || { id: null };
        return setMaterialStatuses(selected);
    };

    const handleGasRecoverStatusSelect = (id) => {
        const selected = recoverGasStatusDropdown.find(mts => mts.id === id) || { id: null };
        return setRecoverGasStatus(selected);
    }


    const DataTable = ({ open, columns, header = null }) => (
        <Collapse in={open}>
            {header && (
                <Toolbar>
                    <Typography
                        sx={{ flex: '1 1 100%', fontFamily: trakrefFontFamily }}
                        variant="h6"
                        id="tableTitle"
                        component="div"
                    >
                        {header}
                    </Typography>
                </Toolbar>
            )}
            <TableContainer component={Paper}>
                <Table size="small" aria-label="gas transfer table">
                    <TableHead>
                        <TableRow>
                            {columns.map((item) => (
                                <TableCell align="center">{item.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow
                            sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                background: lightGrayColor
                            }}
                        >
                            {columns.map((item) => (
                                <TableCell align="center">{item.value}</TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Collapse>
    );

    const determineWeightForWarningText = () => {
        // currentGasWeightLbs = gas weight of selected cylinder
        let weight = currentGasWeightLbs;
        if (transferAmountGreaterThanAssetMaxCapacity) weight = assetMaxGasCapacity;
        if (transferAmountGreaterThanNewAssetMaxCapacity) weight = newGasChargeCapacityLbs;
        return weight;
    };

    return (
        <CommonModal 
            open={open}
            close={() => {
                resetState();
                closeModal(false);
            }}
            header={header}
            bottom={24}
            scroll="paper"
        >
            <Grid container spacing={2} style={{ marginTop: '1%', padding: '0% 8%' }}>
                <Grid item xs={12} lg={12} align='center' sx={{ marginBottom: selectedCylinder.id !== null ? '1%' : '7%' }}>
                    <SearchableDropdown
                        required
                        variant="standard"
                        options={cylinders}
                        label={TranslateW("installGas.cylinder") }
                        value={selectedCylinder.id}
                        onChange={handleCylinderSelect}
                        searchFields={['name', 'label']}
                        getOptionDisabled={(option) => cylindersAlreadySelected.includes(option.id)}
                    />
                </Grid>
                {selectedCylinder.id !== null && (
                    <>
                        <Grid item xs={12} lg={12} align='center'sx={{ marginBottom: isRetroFit ? '7%' : '0%' }}>
                            <DataTable open columns={cylinderColumns} />
                        </Grid>
                        {!isRetroFit && (
                            <Grid item xs={12} lg={12} align='center' sx={{ marginBottom: '7%'}}>
                                <DataTable open columns={assetColumns} header={name}/>
                            </Grid>
                        )}
                    </>
                )}
                <WeightInput
                    required
                    removePadding
                    showConversionArrows
                    header={TranslateW("installGas.transfer-amount") }
                    variant="standard"
                    value={recoverAmount}
                    onChange={onChange}
                    disabled={id === null}
                    columnSpan={5}
                />
                {isUnknownCylinder && isInstall && 
                    (<Grid item xs={12} lg={12} align='center'>                      
                        <Select
                            variant="standard"
                            options={materialStatusesDropdown}
                            label={TranslateW("rapidCylinderEntry.cylinderInputForm.material-status") }
                            value={materialStatuses.id}
                            onChange={e => handleMaterialStatusSelect(e.target.value)}
                        />
                    </Grid>)
                }
                {(isUnknownCylinder && isRecover) && 
                    (<Grid item xs={12} lg={12} align='center'>                      
                        <Select
                            variant="standard"
                            options={recoverGasStatusDropdown}
                            label={TranslateW("rapidCylinderEntry.cylinderInputForm.recover-gas-status") }
                            value={recoverGasStatus.id}
                            onChange={e => handleGasRecoverStatusSelect(e.target.value)}
                        />
                    </Grid>)
                }
                <Grid item xs={12} lg={12} align='center'>                        
                    <Input
                        fullWidth
                        value={cylinderNotes}
                        onChange={e => setCylinderNotes(e.target.value)}
                        label={TranslateW("installGas.cylinder-notes") }
                        maxCharacterLength={50}
                        disabled={id === null}
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12} lg={12} align='center'>
                    <Input
                        fullWidth
                        value={transferNotes}
                        onChange={e => setTransferNotes(e.target.value)}
                        label={TranslateW("installGas.tranfer-notes") }
                        maxCharacterLength={50}
                        disabled={id === null}
                        variant="standard"
                    />
                </Grid>
                {recoverGasError && (
                    <Grid item xs={12} md={12} lg={12}>
                        <Alert severity="error">
                            {Translate("RecoveryAmountLimit", { weight: renderWeight(assetMaxGasCapacity)})}
                            {/*{`Recovery Amount Cannot Exceed ${renderWeight(assetMaxGasCapacity)}`}*/}
                        </Alert>
                    </Grid>
                )}
                {(installGasError || transferAmountGreaterThanAssetMaxCapacity) && (
                    <Grid item xs={12} md={12} lg={12}>
                        <Alert severity="error">
                            {/*{`Install amount cannot exceed ${currentGasWeightLbs} lbs.`}*/}
                            {Translate("InstallAmountLimit", { weight: renderWeight(determineWeightForWarningText()) })}
                        </Alert>
                    </Grid>
                )}
                {transferAmountGreaterThanCylinderWeight && (
                    <Grid item xs={12} md={12} lg={12}>
                        <Alert severity="error">
                            {TranslateW("AmountTransferLimit") }
                        </Alert>
                    </Grid>
                )}
                <Grid item xs={12} lg={12} align='center'>
                    <Box mt={2} mb={2} sx={{ width: '100%' }}>
                        <SecondaryButton
                            fullWidth
                            useLargeButton
                            buttonText={buttonText}
                            onClick={handleClick}
                            disabled={disableSubmitButton}
                        />
                    </Box>
                </Grid>
            </Grid>
        </CommonModal>
    );
};

GasModal.propTypes = {
    id: PropTypes.string.isRequired,
    open: PropTypes.bool.isRequired,
    header: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    closeModal: PropTypes.func.isRequired,
    setDataRefresh: PropTypes.func.isRequired,
    cylinders: PropTypes.array.isRequired,
    buttonText: PropTypes.string,
};

GasModal.defaultProps = {
    buttonText: "Transfer"
};

export default GasModal;
